import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { WriteReceipt } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { splitDt } from '../lib/filters';

/**
 * "Hotovo" — the tick on the legacy reservation form, put where the job is
 * listed: on the Služby card row and on the detail's service checklist.
 * One press; no form. On success the button becomes "✔ hotovo HH:MM" and
 * every query refetches, so the row leaves the Služby card by itself.
 */
export function ServiceDoneButton({
  reservationId,
  serviceId,
  compact,
}: {
  reservationId: number;
  serviceId: number;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const [receipt, setReceipt] = useState<WriteReceipt | null>(null);
  const m = useMutation({
    mutationFn: () => api.serviceDone(reservationId, serviceId),
    onSuccess: async (rc) => {
      setReceipt(rc);
      await qc.invalidateQueries();
    },
  });

  if (receipt) {
    return (
      <span className="arrived-mark" title={`Hotovo ${receipt.at}`}>
        ✔ hotovo {splitDt(receipt.at).t}
      </span>
    );
  }
  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ??
        (err.code === 'writes_disabled'
          ? 'Zápisy jsou na tomto serveru vypnuté.'
          : err.code === 'forbidden'
            ? 'Na tuhle akci nemáte oprávnění.'
            : err.message)
      : err instanceof Error
        ? err.message
        : null;

  return (
    <span className="svcdone" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={compact ? 'btn sm primary' : 'btn primary'}
        disabled={m.isPending}
        onClick={() => {
          if (!m.isPending) m.mutate();
        }}
      >
        {m.isPending ? 'Ukládám…' : 'Hotovo'}
      </button>
      {message && (
        <span className="arrive-error" role="alert">
          {message}
        </span>
      )}
    </span>
  );
}
